import { useState } from 'react';

interface IToggleableColumnDefinition<TField extends string> {
  field: TField;
  defaultHidden?: boolean;
}

interface IStoredSelection<TField extends string> {
  // The fields that were visible at the time of storing.
  visibleFields: TField[];
  // The full set of fields known at the time of storing, used to tell "explicitly
  // hidden" fields apart from fields added to the column definition afterwards.
  knownFields: TField[];
}

function defaultVisibleFields<TField extends string>(columns: IToggleableColumnDefinition<TField>[]): TField[] {
  return columns.filter((column) => !column.defaultHidden).map((column) => column.field);
}

function isStoredSelection<TField extends string>(value: unknown): value is IStoredSelection<TField> {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  const isStringArray = (input: unknown): input is TField[] =>
    Array.isArray(input) && input.every((item) => typeof item === 'string');
  return isStringArray(candidate.visibleFields) && isStringArray(candidate.knownFields);
}

function readStoredFields<TField extends string>(
  storageKey: string,
  columns: IToggleableColumnDefinition<TField>[]
): TField[] {
  const fallback = defaultVisibleFields(columns);

  let raw: string | null;
  try {
    raw = localStorage.getItem(storageKey);
  } catch {
    // localStorage can throw (e.g. disabled, private browsing quota) - fall back silently.
    return fallback;
  }

  if (!raw) {
    return fallback;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return fallback;
  }

  if (!isStoredSelection<TField>(parsed)) {
    return fallback;
  }

  const visibleAtStoreTime = new Set(parsed.visibleFields);
  const knownAtStoreTime = new Set(parsed.knownFields);

  // Merge: for fields known when the value was stored, respect the stored show/hide
  // choice. Fields removed from the current definition are dropped. Fields added since
  // storing (not in knownAtStoreTime) fall back to their own defaultHidden.
  return columns
    .filter((column) =>
      knownAtStoreTime.has(column.field) ? visibleAtStoreTime.has(column.field) : !column.defaultHidden
    )
    .map((column) => column.field);
}

function writeStoredFields<TField extends string>(
  storageKey: string,
  visibleFields: TField[],
  columns: IToggleableColumnDefinition<TField>[]
) {
  const selection: IStoredSelection<TField> = {
    visibleFields,
    knownFields: columns.map((column) => column.field),
  };
  try {
    localStorage.setItem(storageKey, JSON.stringify(selection));
  } catch {
    // Ignore write failures (e.g. storage disabled/full) - selection still applies for this session.
  }
}

/**
 * Remembers a set of toggleable column fields across sessions via `localStorage`
 * (browser-scoped, not synced to the user's account - see docs/adr/0007).
 *
 * Reconciles the stored selection against the current `columns` definition: columns
 * removed since storing are dropped, and columns added since storing fall back to their
 * own `defaultHidden` instead of the stored (nonexistent) choice.
 */
export function useToggleableColumns<TField extends string>(
  storageKey: string,
  columns: IToggleableColumnDefinition<TField>[]
): [TField[], (fields: TField[]) => void] {
  const [visibleFields, setVisibleFieldsState] = useState<TField[]>(() => readStoredFields(storageKey, columns));

  function setVisibleFields(fields: TField[]) {
    setVisibleFieldsState(fields);
    writeStoredFields(storageKey, fields, columns);
  }

  return [visibleFields, setVisibleFields];
}
